import React from "react";

import { Link as RouterLink } from "react-router-dom";

import {
  Box,
  Grid,
  Typography,
  Button,
  ButtonGroup,
  Hidden,
} from "@material-ui/core";

import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineContent,
  TimelineOppositeContent,
  TimelineConnector,
  TimelineDot,
  Alert,
} from "@material-ui/lab";

import Countdown from "../Countdown";

import routes, {
  RANKS,
  SEASON_PREDICTIONS,
  MY_ENTRY,
  WEEKLY_PREDICTIONS,
} from "../../data/routes";

function InProgressSeason({
  season,
  baseUrl,
  user,
  rankingsComplete,
  seasonPredictionsComplete,
  weeklyPredictionsComplete,
  isUserComplete,
  onSignUpClick,
  onSignInClick,
  onSettingsClick,
}) {
  const {
    name,
    isBachelorette,
    isSeasonPredictionsLocked,
    isRankingsLocked,
    isWeeklyPredictionsLocked,
    currentWeeklyPredictionsWeek,
    nextEpisodeAirDate,
    nextEpisodeAirDateEnglish,
    secondEpisodeAirDate,
    secondEpisodeAirDateEnglish,
  } = season;

  return (
    <>
      {user && !isWeeklyPredictionsLocked && (
        <Box mb={8}>
          <Typography variant="h3" gutterBottom>
            Week {currentWeeklyPredictionsWeek} predictions are up!
          </Typography>
          <Typography variant="h5" gutterBottom>
            Fill them out before you watch{" "}
            <strong>Episode {currentWeeklyPredictionsWeek}</strong>, which airs
            on <strong>{nextEpisodeAirDateEnglish}</strong>:
          </Typography>
          <Hidden smDown>
            <Box py={3}>
              <Countdown endTime={nextEpisodeAirDate} />
            </Box>
          </Hidden>
          <Alert
            severity={weeklyPredictionsComplete ? "success" : "warning"}
            action={
              <Button
                size="small"
                color={weeklyPredictionsComplete ? "inherit" : "primary"}
                variant={weeklyPredictionsComplete ? "outlined" : "contained"}
                disableElevation
                component={RouterLink}
                to={`${baseUrl}${routes[WEEKLY_PREDICTIONS].path}`}
              >
                View Weekly Predictions
              </Button>
            }
          >
            {weeklyPredictionsComplete ? (
              <strong>
                You're finished with your weekly predictions for week{" "}
                {currentWeeklyPredictionsWeek}!
              </strong>
            ) : (
              <strong>Make sure you fill out your weekly predictions!</strong>
            )}
          </Alert>
        </Box>
      )}

      <Typography variant="h4" gutterBottom>
        Join us for {name}'s {isBachelorette ? "Bachelorette" : "Bachelor"}{" "}
        season!
      </Typography>

      {!isRankingsLocked && !isSeasonPredictionsLocked && (
        <Box py={2}>
          <Typography variant="h5" gutterBottom>
            Complete your entry before you watch <strong>Episode 2</strong>,
            which airs on <strong>{secondEpisodeAirDateEnglish}</strong>:
          </Typography>
          <Hidden smDown>
            <Box pt={1}>
              <Countdown endTime={secondEpisodeAirDate} />
            </Box>
          </Hidden>
        </Box>
      )}

      <Typography variant="h5" gutterBottom>
        Here's how to play along:
      </Typography>

      <Timeline align="left">
        <TimelineItem>
          <TimelineOppositeContent style={{ flex: 0 }} />
          <TimelineSeparator>
            <TimelineDot variant="outlined" color="primary" />
            <TimelineConnector />
          </TimelineSeparator>
          <TimelineContent>
            <Box mt={-1} mb={5}>
              <Typography variant="h5" gutterBottom>
                Rank your favorite contestants
              </Typography>
              <Typography variant="body1" paragraph>
                Each week, you'll get points if they're still in the show!
              </Typography>
              {user && !isRankingsLocked && (
                <Alert
                  severity={rankingsComplete ? "success" : "warning"}
                  action={
                    <Button
                      size="small"
                      color={rankingsComplete ? "inherit" : "primary"}
                      variant={rankingsComplete ? "outlined" : "contained"}
                      disableElevation
                      component={RouterLink}
                      to={`${baseUrl}${routes[RANKS].path}`}
                    >
                      View Rankings
                    </Button>
                  }
                >
                  {rankingsComplete ? (
                    <strong>You're finished with your contestant rankings!</strong>
                  ) : (
                    <strong>You still need to finish ranking the contestants!</strong>
                  )}
                </Alert>
              )}
            </Box>
          </TimelineContent>
        </TimelineItem>

        <TimelineItem>
          <TimelineOppositeContent style={{ flex: 0 }} />
          <TimelineSeparator>
            <TimelineDot variant="outlined" color="primary" />
            <TimelineConnector />
          </TimelineSeparator>
          <TimelineContent>
            <Box mt={-1} mb={5}>
              <Typography variant="h5" gutterBottom>
                Answer some season-long trivia questions
              </Typography>
              <Typography variant="body1" paragraph>
                You'll get points at the end of the season if you correctly
                predict events on the show!
              </Typography>
              {user && !isSeasonPredictionsLocked && (
                <Alert
                  severity={seasonPredictionsComplete ? "success" : "warning"}
                  action={
                    <Button
                      size="small"
                      color={seasonPredictionsComplete ? "inherit" : "primary"}
                      variant={
                        seasonPredictionsComplete ? "outlined" : "contained"
                      }
                      disableElevation
                      component={RouterLink}
                      to={`${baseUrl}${routes[SEASON_PREDICTIONS].path}`}
                    >
                      View Predictions
                    </Button>
                  }
                >
                  {seasonPredictionsComplete ? (
                    <strong>You're finished with your season predictions!</strong>
                  ) : (
                    <strong>
                      You still need to finish your season predictions!
                    </strong>
                  )}
                </Alert>
              )}
            </Box>
          </TimelineContent>
        </TimelineItem>

        {user && (
          <TimelineItem>
            <TimelineOppositeContent style={{ flex: 0 }} />
            <TimelineSeparator>
              <TimelineDot variant="outlined" color="primary" />
              <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent>
              <Box mt={-1} mb={5}>
                <Typography variant="h5" gutterBottom>
                  Fill out your profile
                </Typography>
                <Typography variant="body1" paragraph>
                  You can set a name and profile picture that will show in the
                  standings!
                </Typography>
                <Alert
                  severity={isUserComplete ? "success" : "warning"}
                  action={
                    <Button
                      size="small"
                      color={isUserComplete ? "inherit" : "primary"}
                      variant={isUserComplete ? "outlined" : "contained"}
                      disableElevation
                      onClick={onSettingsClick}
                    >
                      Edit Profile
                    </Button>
                  }
                >
                  {isUserComplete ? (
                    <strong>Your profile is all set up!</strong>
                  ) : (
                    <strong>You still need to set up your profile!</strong>
                  )}
                </Alert>
              </Box>
            </TimelineContent>
          </TimelineItem>
        )}

        <TimelineItem>
          <TimelineOppositeContent style={{ flex: 0 }} />
          <TimelineSeparator>
            <TimelineDot variant="outlined" color="primary" />
            {!user && <TimelineConnector />}
          </TimelineSeparator>
          <TimelineContent>
            <Box mt={-1} mb={5}>
              <Typography variant="h5" gutterBottom>
                Follow along week to week
              </Typography>
              <Typography variant="body1" paragraph>
                Each week, you can earn additional points for guessing what
                happens in each episode!
              </Typography>
              {user && !isWeeklyPredictionsLocked && (
                <Alert
                  severity={weeklyPredictionsComplete ? "success" : "warning"}
                  action={
                    <Button
                      size="small"
                      color={weeklyPredictionsComplete ? "inherit" : "primary"}
                      variant={
                        weeklyPredictionsComplete ? "outlined" : "contained"
                      }
                      disableElevation
                      component={RouterLink}
                      to={`${baseUrl}${routes[WEEKLY_PREDICTIONS].path}`}
                    >
                      View Weekly Predictions
                    </Button>
                  }
                >
                  {weeklyPredictionsComplete ? (
                    <strong>
                      You're finished with your weekly predictions for week{" "}
                      {currentWeeklyPredictionsWeek}!
                    </strong>
                  ) : (
                    <strong>
                      Make sure you fill out your weekly predictions!
                    </strong>
                  )}
                </Alert>
              )}
            </Box>
          </TimelineContent>
        </TimelineItem>

        {!user && (
          <TimelineItem>
            <TimelineOppositeContent style={{ flex: 0 }} />
            <TimelineSeparator>
              <TimelineDot variant="outlined" color="primary" />
            </TimelineSeparator>
            <TimelineContent>
              <Box mt={-1} mb={5}>
                <ButtonGroup color="primary" variant="contained">
                  <Button onClick={onSignUpClick}>Sign up</Button>
                  <Button onClick={onSignInClick}>Sign in</Button>
                </ButtonGroup>
              </Box>
            </TimelineContent>
          </TimelineItem>
        )}
      </Timeline>
    </>
  );
}

export default InProgressSeason;